import type { Tool } from '@/features/traceability/type/Tool';
import type { ToolAlias } from '@/features/traceability/api/ToolAliasApiClient.types';

export interface ToolReferenceGroup {
  reference: string;
  tools: Tool[];
  alias?: ToolAlias;
}

export interface GroupedTools {
  referenced: ToolReferenceGroup[];
  unreferenced: Tool[];
}

/**
 * Groups a company's tools by their reference for the admin deletion tab.
 * The listing is the complete union of both reference sources: tools' `reference`
 * fields AND alias rows — a reference existing only as an alias (zero tools) is
 * listed too, so every deletable case is visible (user decision 2026-09-08).
 * Tools with a null/blank reference land in `unreferenced` — they can only be
 * deleted one by one (the backend rejects blank references for the same reason).
 */
export function groupToolsByReference(tools: Tool[], aliases: ToolAlias[] = []): GroupedTools {
  const byReference = new Map<string, Tool[]>();
  const unreferenced: Tool[] = [];

  for (const tool of tools) {
    const reference = tool.reference?.trim();
    if (!reference) {
      unreferenced.push(tool);
    } else {
      const group = byReference.get(reference);
      if (group) {
        group.push(tool);
      } else {
        byReference.set(reference, [tool]);
      }
    }
  }

  const aliasByReference = new Map<string, ToolAlias>();
  for (const alias of aliases) {
    const reference = alias.reference?.trim();
    if (!reference) continue;
    aliasByReference.set(reference, alias);
    if (!byReference.has(reference)) byReference.set(reference, []);
  }

  const byToolId = (a: Tool, b: Tool) => a.toolId.localeCompare(b.toolId);
  const referenced = [...byReference.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([reference, groupTools]) => ({
      reference,
      tools: groupTools.sort(byToolId),
      alias: aliasByReference.get(reference),
    }));

  return { referenced, unreferenced: unreferenced.sort(byToolId) };
}
